import type {ReactElement} from 'react';
import {useEffect, useState} from 'react';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import {Dropdown} from '@Components/dropdown-v2';
import {ALIGNMENT_TYPE, DROPDOWN_POSITION} from '@Components/dropdown-v2/dropdown.types';
import {ControlledList} from '@Components/controlled-list';
import type {ControlledListItemType} from '@Components/controlled-list/controlled-list.types';
import {CONTROLLED_LIST_ITEM_TYPE, ControlledListItemSize} from '@Components/controlled-list/controlled-list.types';
import Divider from '@Components/divider';
import {MOBILE_MODAL_WIDTH_THRESHOLD} from '@Components/modal';
import {MODALS_IDS} from '@Components/modal-container';
import {closeModal} from '@Components/modal-container/modal-container-reducer';
import useWindowSize from '@Hooks/useWindowSize';
import {useAppDispatch, useAppSelector} from '@/hooks';
import {StockFilterTypes, DEFAULT_APPLIED_SUBFILTER, StockPanelTabs} from '@Panels/stock-panel/stock-panel.types';
import type {StockFilter, StockFilterID} from '@Panels/stock-panel/stock-panel.types';
import type {StockMediaFiltersProps} from './stock-media-filters.types';
import {StockMediaFilterPill} from './components/stock-media-filter-pill/stock-media-filter-pill';
import {StockMediaFilterPillType} from './components/stock-media-filter-pill/stock-media-filter-pill.types';
import styles from './stock-media-filters.module.scss';

export function StockMediaFilters({...props}: StockMediaFiltersProps): ReactElement | null {
  const [dropdownOpenFilter, setDropdownOpenFilter] = useState<StockFilterID | undefined>(undefined);
  const dispatch = useAppDispatch();
  const {windowWidth} = useWindowSize();

  // Below this width the Dropdown renders its popup inside a MoreOptionsModal (showModalForSmallScreen). That modal is
  // opened imperatively, so the controlled `open` state only closes the desktop popup — the modal must be closed explicitly.
  const closeSmallScreenFilterModal = (): void => {
    if (windowWidth < MOBILE_MODAL_WIDTH_THRESHOLD) {
      dispatch(closeModal(MODALS_IDS.MORE_OPTIONS_MODAL));
    }
  };

  const isOpenFilterModalShowing = useAppSelector((state): boolean => {
    if (dropdownOpenFilter === undefined) {
      return false;
    }
    const modal = state.modals.modalsHashmap[MODALS_IDS.MORE_OPTIONS_MODAL];
    if (!modal?.isOpen) {
      return false;
    }
    return modal.panelProps?.panelId === dropdownOpenFilter;
  });

  // Dismissing the small-screen modal without picking (outside tap / swipe) closes it through the modal system,
  // which never fires the Dropdown's onClose — so the pill's open state must be cleared from the modal's Redux state.
  useEffect(() => {
    if (windowWidth < MOBILE_MODAL_WIDTH_THRESHOLD && dropdownOpenFilter !== undefined && !isOpenFilterModalShowing) {
      setDropdownOpenFilter(undefined);
    }
  }, [windowWidth, dropdownOpenFilter, isOpenFilterModalShowing]);

  const isSubFilterSelected = (filterID: string, subFilterID: string): boolean => {
    return props.selectedFilters.some((f) => f.filter === filterID && f.subFilter === subFilterID);
  };

  const getFilterDivider = (key: string): ReactElement => {
    return (
      <li key={key} className={styles.dividerItem} aria-hidden>
        <Divider width={1} height={20} />
      </li>
    );
  };

  const getButtonFilterPills = (filter: StockFilter): ReactElement[] => {
    return filter.subFilters.map((subFilter): ReactElement => {
      const isSelected = isSubFilterSelected(filter.id, subFilter.id);
      return (
        <StockMediaFilterPill
          key={`${filter.id}-${subFilter.id}`}
          type={StockMediaFilterPillType.LIST_ITEM}
          isSelected={isSelected}
          onClick={(): void => {
            props.onFilterItemClicked(filter.id, isSelected ? null : subFilter.id);
          }}
        >
          <Text val={subFilter.text} size={TextSize.SM} bold />
          {isSelected ? (
            <Icon
              icon="icon-close"
              onClick={(e): void => {
                e.stopPropagation();
                props.onFilterItemClicked(filter.id, null);
              }}
              size={IconSize.SIZE_ICON_16}
              className="content-body"
            />
          ) : null}
        </StockMediaFilterPill>
      );
    });
  };

  const getDropdownFilterPill = (filter: StockFilter): ReactElement => {
    const selectedEntry = props.selectedFilters.find((f) => f.filter === (filter.id as string));
    const isSelected = selectedEntry?.subFilter !== undefined && selectedEntry.subFilter !== null && selectedEntry.subFilter !== '';
    const selectedLabel = isSelected ? filter.subFilters.find((sf) => sf.id === selectedEntry?.subFilter)?.text : undefined;
    const isOpen = dropdownOpenFilter !== undefined && dropdownOpenFilter === filter.id;

    const listItems: ControlledListItemType[] = [
      {
        id: DEFAULT_APPLIED_SUBFILTER.id,
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_3,
        text: window.i18next.t('pmwjs_all'),
        isSelected: !isSelected,
        listSize: ControlledListItemSize.SMALL,
      },
      ...filter.subFilters.map(
        (sf): ControlledListItemType => ({
          id: sf.id,
          type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_3,
          text: sf.text,
          isSelected: selectedEntry?.subFilter === sf.id,
          listSize: ControlledListItemSize.SMALL,
        })
      ),
    ];

    return (
      <Dropdown
        key={filter.id}
        id={filter.id}
        open={isOpen}
        onOpen={(): void => {
          setDropdownOpenFilter(filter.id);
        }}
        onClose={(): void => {
          // A mousedown inside THIS pill's open popup is an outside click for every sibling pill's Dropdown, which
          // fires their onClose too. Only the pill that is actually open may clear the shared state — otherwise the
          // open popup unmounts on mousedown and the option's click never lands (multi-dropdown tabs like Audio).
          setDropdownOpenFilter((prev) => (prev === filter.id ? undefined : prev));
        }}
        doCloseOnInnerClick={false}
        panelIdForModal={filter.id}
        popupSpacingClass="spacing-p-t-2 spacing-p-l-2 spacing-p-r-2"
        alignment={ALIGNMENT_TYPE.BOTTOM_START}
        popUpHasCustomWidth
        popupClassName={styles.dropdownPopup}
        className={styles.dropdownItem}
        position={DROPDOWN_POSITION.FIXED}
        selector={
          <StockMediaFilterPill type={StockMediaFilterPillType.DROPDOWN_SELECTOR} isSelected={isSelected} isActive={isOpen}>
            <Text val={selectedLabel ?? filter.name} size={TextSize.SM} bold />
            {isSelected ? (
              <Icon
                icon="icon-close"
                onClick={(e): void => {
                  e.stopPropagation();
                  props.onFilterItemClicked(filter.id, null);
                  setDropdownOpenFilter(undefined);
                  // The click focuses the pill's ClickableDiv selector, which would keep the :focus-within style stuck on the reset pill.
                  if (document.activeElement instanceof HTMLElement) {
                    document.activeElement.blur();
                  }
                }}
                size={IconSize.SIZE_ICON_16}
                className="content-body"
              />
            ) : (
              <Icon icon={isOpen ? 'icon-caret-up' : 'icon-caret-down'} size={IconSize.SIZE_ICON_16} className="content-body" />
            )}
          </StockMediaFilterPill>
        }
        popup={
          <ControlledList
            onClick={(subFilterId, e): void => {
              e?.preventDefault();
              e?.stopPropagation();
              props.onFilterItemClicked(filter.id, subFilterId === DEFAULT_APPLIED_SUBFILTER.id ? null : subFilterId);
              setDropdownOpenFilter(undefined);
              closeSmallScreenFilterModal();
            }}
            className="spacing-m-0"
            id={filter.id}
            items={listItems}
          />
        }
        stopClickPropagation
        showModalForSmallScreen
        modalTitle={filter.name}
      />
    );
  };

  const renderFilter = (filter: StockFilter): ReactElement | ReactElement[] => {
    if (filter.type === StockFilterTypes.DROPDOWN) {
      return getDropdownFilterPill(filter);
    }
    return getButtonFilterPills(filter);
  };

  const getVisibleFilters = (): StockFilter[] => {
    if (props.keepFiltersHiddenForTab[props.selectedTab]) {
      return [];
    }
    const allowedFilters = props.allowedFiltersForTab?.[props.selectedTab];
    return props.filtersForSelectedTab.filter((f) => f.subFilters.length > 0 && (allowedFilters === undefined || allowedFilters.includes(f.id)));
  };

  const getFiltersSection = (visibleFilters: StockFilter[]): ReactElement | null => {
    if (!visibleFilters.length) {
      return null;
    }
    // The audio tab has many independent dropdown filters (Mood / SFX / Genre / ...) where dividers add clutter, so it opts out.
    const showDividers = props.selectedTab !== StockPanelTabs.AUDIOS;
    return (
      <>
        {visibleFilters.flatMap((filter, index): ReactElement[] => {
          const pills = renderFilter(filter);
          const pillArray = Array.isArray(pills) ? pills : [pills];
          // A divider separates independent filter groups (e.g. Pricing | Animation); never before the first group.
          const showLeadingDivider = showDividers && index > 0;
          if (!showLeadingDivider) {
            return pillArray;
          }
          return [getFilterDivider(`divider-${filter.id}`), ...pillArray];
        })}
      </>
    );
  };

  const visibleFilters = getVisibleFilters();
  if (!visibleFilters.length) {
    return null;
  }

  return (
    <div className={`${styles.container} spacing-m-t-3`}>
      <ul className={`flex-h-row spacing-m-b-0 spacing-p-l-4 spacing-p-r-4 ${styles.list}`}>{getFiltersSection(visibleFilters)}</ul>
    </div>
  );
}
